import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// Configuración del Asistente APS: preferencias, memoria y estado del proveedor.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Tabs, Tab, Stack, MenuItem, TextField,
  Switch, FormControlLabel, Button, Chip, CircularProgress, Alert, Divider,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AIMemoryEditor from '../components/ai/AIMemoryEditor';

export default function AISettingsPage() {
  const { t } = useTranslation();
  const { isAdminOrSuperadmin, isManager } = useAuth();
  const isManagerRole = (isManager && isManager()) || (isAdminOrSuperadmin && isAdminOrSuperadmin());
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([api.ai.getSettings(), api.ai.status()]);
      setSettings(s.data);
      setStatus(st.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    setSettings((p) => ({ ...p, ...patch }));
    setSaving(true);
    try { const r = await api.ai.updateSettings(patch); setSettings(r.data); }
    catch (e) { /* noop */ }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    try { const r = await api.ai.status(); setStatus(r.data); }
    catch (e) { /* noop */ }
    finally { setTesting(false); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="md" disableGutters>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SmartToyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>{t('hardcoded.pages_aisettingspage.preferencias_de_ia')}</Typography>
      </Box>

      {settings && !settings.ai_enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('hardcoded.pages_aisettingspage.el_asistente_ia_esta_deshabilitado')}</Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={t('hardcoded.components_ai_assistantpanel.preferencias')} />
          <Tab label={t('hardcoded.components_ai_assistantpanel.memoria')} />
          <Tab label={t('hardcoded.pages_aisettingspage.proveedor')} />
        </Tabs>
      </Paper>

      {tab === 0 && settings && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <TextField select label={t('hardcoded.pages_aisettingspage.nivel_de_detalle')} value={settings.detail_mode || 'normal'}
              onChange={(e) => update({ detail_mode: e.target.value })} sx={{ maxWidth: 280 }}>
              <MenuItem value="breve">{t('pages_aisettingspage.menu_breve')}</MenuItem>
              <MenuItem value="normal">{t('pages_aisettingspage.menu_normal')}</MenuItem>
              <MenuItem value="tecnico">{t('hardcoded.pages_aisettingspage.tecnico')}</MenuItem>
            </TextField>
            <TextField select label={t('hardcoded.pages_aisettingspage.idioma_preferido')} value={settings.language || 'es'}
              onChange={(e) => update({ language: e.target.value })} sx={{ maxWidth: 280 }}>
              <MenuItem value="es">{t('profile.languages.es')}</MenuItem>
              <MenuItem value="en">{t('pages_aisettingspage.menu_english')}</MenuItem>
              <MenuItem value="fr">{t('pages_aisettingspage.menu_french')}</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch checked={!!settings.memory_enabled}
              onChange={(e) => update({ memory_enabled: e.target.checked })} />} label={t('hardcoded.pages_aisettingspage.activar_memoria_personal')} />
            <FormControlLabel control={<Switch checked={!!settings.recommendations_enabled}
              onChange={(e) => update({ recommendations_enabled: e.target.checked })} />} label={t('hardcoded.pages_aisettingspage.activar_recomendaciones')} />
            {saving && <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_operators_operatorquickcreateform.guardando')}</Typography>}
          </Stack>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 3 }}><AIMemoryEditor isManager={isManagerRole} /></Paper>
      )}

      {tab === 2 && status && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={1.5}>
            <Row label={t('hardcoded.pages_aisettingspage.proveedor')} value={status.provider} />
            <Row label={t('gps.form.model')} value={status.model} />
            <Row label={t('hardcoded.pages_aisettingspage.ia_habilitada')} chip={status.ai_enabled ? t('common.yes') : t('common.no')} chipColor={status.ai_enabled ? 'success' : 'default'} />
            <Row label={t('hardcoded.pages_aisettingspage.api_key_configurada')} chip={status.configured ? t('common.yes') : t('common.no')} chipColor={status.configured ? 'success' : 'error'} />
            <Row label={t('pages_aisettingspage.label_streaming')} chip={status.streaming ? t('common.active') : t('common.inactive')} chipColor={status.streaming ? 'success' : 'default'} />
            {status.health && (
              <>
                <Divider />
                <Row label={t('hardcoded.pages_aisettingspage.test_de_conexion')} chip={status.health.ok ? t('common.ok') : t('common.error')} chipColor={status.health.ok ? 'success' : 'error'} />
                {!status.health.ok && status.health.reason && (
                  <Typography variant="caption" color="error">{status.health.reason}</Typography>
                )}
              </>
            )}
            {status.health !== undefined && (
              <Box><Button variant="outlined" onClick={runTest} disabled={testing} startIcon={testing ? <CircularProgress size={16} /> : null}>{t('hardcoded.pages_aisettingspage.probar_conexion')}</Button></Box>
            )}
            {status.health === undefined && (
              <Alert severity="info">{t('hardcoded.pages_aisettingspage.el_test_de_conexion_solo')}</Alert>
            )}
          </Stack>
        </Paper>
      )}
    </Container>
  );
}

function Row({ label, value, chip, chipColor }) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" sx={{ minWidth: 200, color: 'text.secondary' }}>{label}</Typography>
      {chip ? <Chip size="small" label={chip} color={chipColor || 'default'} /> : <Typography variant="body2" fontWeight={600}>{value}</Typography>}
    </Stack>
  );
}
